<script setup lang="ts">
import { computed, reactive } from "vue";
import Combobox from "@/components/ui/Combobox.vue";
import Alert from "@/components/ui/Alert.vue";

enum ImportType {
  Both = "both",
  Product = "product",
  Equipment = "equipment"
}

interface InitialData {
  type?: string;
  update_only?: boolean;
  unique_key?: string;
  product_type?: number | null;
  column_matching?: Record<string, any>;
  equipment_status?: string | null;
  equipment_health_state?: string | null;
  park?: number | null;
}

interface TypeOption {
  value: string;
  label: string;
}

interface ProductType {
  id: number;
  name: string;
}

interface FieldOption {
  id: string;
  label: string;
  example?: string | null;
}

interface TranslatableField {
  [key: string]: string;
}

interface Locale {
  value: string;
  label: string;
  emoji: string;
}

interface EquipmentField {
  [key: string]: string;
}

interface StatusOption {
  value: string;
  label: string;
}

interface Park {
  id: number;
  text: string;
}

interface UniqueKey {
  value: string;
  label: string;
}

interface FormData {
  type: ImportType | undefined;
  update_only: boolean;
  unique_key: string | undefined;
  product_type: number | undefined;
  column_matching: Record<string, any>;
  equipment_status: string | undefined;
  equipment_health_state: string | undefined;
  park: number | undefined;
}

const props = defineProps<{
  types: TypeOption[];
  initialData: InitialData;
  productTypes: ProductType[];
  productFields: Record<string, string>;
  fileHeadingOptions: FieldOption[];
  translatableModelFields: TranslatableField;
  locales: Locale[];
  equipmentFields: EquipmentField;
  equipmentStatuses: StatusOption[] | null;
  equipmentHealthStates: StatusOption[] | null;
  parks: Park[];
  uniqueKeys: UniqueKey[];
}>();

const formData = reactive<FormData>({
  type: (props.initialData.type as ImportType) ?? undefined,
  update_only: props.initialData.update_only ?? false,
  unique_key: props.initialData.unique_key ?? undefined,
  product_type: props.initialData.product_type ?? undefined,
  column_matching: props.initialData.column_matching ?? {},
  equipment_status: props.initialData.equipment_status ?? undefined,
  equipment_health_state: props.initialData.equipment_health_state ?? undefined,
  park: props.initialData.park ?? undefined,
});

const filteredUniqueKeys = computed(() => {
  if (formData.type === ImportType.Product || formData.type === ImportType.Both) {
    return props.uniqueKeys.filter(uk => Object.keys(props.productFields).includes(uk.value));
  } else if (formData.type === ImportType.Equipment && !formData.update_only) {
    return props.uniqueKeys.filter(uk => Object.keys(props.productFields).includes(uk.value));
  } else if (formData.type === ImportType.Equipment && formData.update_only) {
    return props.uniqueKeys.filter(uk => Object.keys(props.equipmentFields).includes(uk.value));
  }
  return [];
});

// Initialize translatable fields needed
Object.entries(props.translatableModelFields).forEach(([key]) => {
  if (!formData.column_matching[key]) {
    formData.column_matching[key] = {};
  }
  props.locales.forEach(locale => {
    if (locale && locale.value && !formData.column_matching[key][locale.value]) {
      formData.column_matching[key][locale.value] = "";
    }
  });
});

const matchingProductFields = computed(() =>
  (formData.type === ImportType.Product || formData.type === ImportType.Both)
    ? props.productFields
    : Object.fromEntries(
      Object.entries(props.productFields).filter(([key]) => key === formData.unique_key)
    )
);

const showProductFields = computed(() =>
  formData.type === ImportType.Product || formData.type === ImportType.Both
);

const showEquipmentFields = computed(() =>
  formData.type === ImportType.Equipment || formData.type === ImportType.Both
);

const getFieldExample = (field: string): string => {
  const example = props.fileHeadingOptions.find((option) => option.id === formData.column_matching[field]);
  return example?.example ?? "";
};
</script>

<template>
  <div>
    <div class="mb-4">
      <label for="formImportType">
        {{ $t("What do you want to import ?") }}
        <small class="align-middle font-weight-bold text-danger">*</small>
      </label>
      <Combobox
        name="type"
        v-model="formData.type"
        :options="types.map((type) => ({ value: type.value, label: type.label }))"
        :fullwidth="true"
      />
    </div>

    <div class="mb-4 form-check">
      <input type="checkbox" name="update_only" v-model="formData.update_only"
             value="1" class="form-check-input" id="formImportUpdateOnly">
      <label class="form-check-label" for="formImportUpdateOnly">
        {{ $t("Update only") }}
      </label>
      <small class="form-text text-muted">
        {{ $t("If checked, only existing models/items will be updated, new ones will be ignored") }}
      </small>
    </div>

    <hr>

    <!-- Unique Key -->
    <Alert variant="warning">
      <p>
        <strong>⚠️ {{ $t("Important:") }}</strong><br>
        {{ $t("Duplicate Identifiers in your file will be ignored during import to prevent conflicts.") }}<br>
        {{ $t("Make sure each item in your file has a unique Identifier to ensure accurate matching and updates.") }}
      </p>
    </Alert>

    <div class="mb-4">
      <label for="unique_key">{{ $t("How do you want to identify your models or items?") }}</label>
      <small class="align-middle font-weight-bold text-danger">*</small>
        <Combobox
          name="unique_key"
          v-model="formData.unique_key"
          :options="filteredUniqueKeys.map((uniqueKey) => ({ value: uniqueKey.value, label: uniqueKey.label }))"
          :fullwidth="true"
        />
    </div>

    <hr>

    <template v-if="formData.unique_key && formData.type">

      <h4>{{ $t("Select the columns corresponding to the fields") }}</h4>
      <Alert variant="info">
        {{ $t("Match the fields with the columns of the file.") }} <br>
        👉 {{ $t("If a field is not matched, it will not be imported/updated.") }}
      </Alert>


      <template v-if="Object.entries(matchingProductFields).length > 0">
        <h5>{{ $t("Model information") }}</h5>
      </template>

      <template v-if="showProductFields">
        <!-- Product Type -->
        <div class="mb-4">
          <label for="formImportProductType">
            {{ $t("Model category") }}
          </label>
            <Combobox
              name="product_type"
              v-model="formData.product_type"
              :options="productTypes.map((productType) => ({ value: productType.id, label: productType.name }))"
              :fullwidth="true"
            />
        </div>
      </template>

        <!-- Product Fields -->
        <template v-for="(label, field) in matchingProductFields" :key="field">
          <div class="mb-4">
            <label :for="'column_matching[' + field + ']'">{{ label }}</label>
            <Combobox
              :name="'column_matching[' + field + ']'"
              v-model="formData.column_matching[field]"
              :options="fileHeadingOptions.map((option) => ({ value: option.id, label: option.label }))"
              :fullwidth="true"
            />
            <template v-if="getFieldExample(field)">
              <div class="mt-2 d-inline-flex align-items-center px-3 py-1 rounded border bg-light">
                <span class="text-muted tw:text-xs">{{ $t("From file:") }}</span>
                <strong class="ml-1 text-dark">{{ getFieldExample(field) }}</strong>
              </div>
            </template>
          </div>
        </template>

      <template v-if="showProductFields">
        <!-- Translatable Fields -->
        <template v-for="[fieldKey, fieldValue] in Object.entries(translatableModelFields)" :key="fieldKey">
          <template v-for="lang in locales" :key="lang.value">
            <div class="mb-4">
              <label :for="'column_matching[' + fieldKey + '][' + lang.value + ']'">
                {{ fieldValue }} - {{ lang.label }} {{ lang.emoji }}
              </label>
              <Combobox
                :name="'column_matching[' + fieldKey + '][' + lang.value + ']'"
                v-model="formData.column_matching[fieldKey][lang.value]"
                :options="fileHeadingOptions.map((option) => ({ value: option.id, label: option.label }))"
                :fullwidth="true"
              />
            </div>
          </template>
        </template>

      </template>

      <template v-if="showEquipmentFields">

        <h5>{{ $t("Item information") }}</h5>

        <Alert variant="info">
          ℹ️ {{ $t("Auto Generated Barcode") }}<br>
          {{ $t("If no column is selected for Barcode / QR Code, barcodes will be autogenerated for each item.") }}
        </Alert>

        <!-- Equipment Fields -->
        <template v-for="(label, field) in equipmentFields" :key="field">
          <div class="mb-4">
          <label :for="'column_matching[' + field + ']'">{{ label }}</label>
            <Combobox
              :name="'column_matching[' + field + ']'"
              v-model="formData.column_matching[field]"
              :options="fileHeadingOptions.map((option) => ({ value: option.id, label: option.label }))"
              :fullwidth="true"
            />
            <template v-if="getFieldExample(field)">
              <div class="mt-2 d-inline-flex align-items-center px-3 py-1 rounded border bg-light">
                <span class="text-muted tw:text-xs">{{ $t("From file:") }}</span>
                <strong class="ml-1 text-dark">{{ getFieldExample(field) }}</strong>
              </div>
            </template>
          </div>
        </template>

        <!-- Equipment Status -->
        <template v-if="equipmentStatuses">
          <div class="mb-4">
            <label for="formImportEquipmentStatus">
              {{ $t("Item status") }}
            </label>
            <Combobox
              name="equipment_status"
              v-model="formData.equipment_status"
              :options="equipmentStatuses.map((equipmentStatus) => ({ value: equipmentStatus.value, label: equipmentStatus.label }))"
              :fullwidth="true"
            />
          </div>
        </template>

        <!-- Equipment Health States -->
        <template v-if="equipmentHealthStates">
          <div class="mb-4">
            <label for="formImportEquipmentHealthState">
              {{ $t("Item health state") }}
            </label>
            <Combobox
              name="equipment_health_state"
              v-model="formData.equipment_health_state"
              :options="equipmentHealthStates.map((equipmentHealthState) => ({ value: equipmentHealthState.value, label: equipmentHealthState.label }))"
              :fullwidth="true"
            />
          </div>
        </template>

        <!-- Park -->
        <div class="mb-4">
          <label for="park">{{ $t("Add items to park") }}</label>
            <Combobox
              name="park"
              v-model="formData.park"
              :options="parks.map((park) => ({ value: park.id, label: park.text }))"
              :fullwidth="true"
            />
        </div>

      </template>

      <button type="submit" class="btn btn-primary">
        {{ $t("Import") }}
      </button>
    </template>

  </div>
</template>
